import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-monthly-join-landing',
  templateUrl: './monthly-join-landing.component.html',
  styleUrls: ['./monthly-join-landing.component.scss'],
  imports: [MatIconModule, TranslatePipe, MatButtonModule, CommonModule],
})
export class MonthlyJoinLandingComponent implements OnInit {
  successMessages: string[] = [];
  errorMessages: string[] = [];
  limitMessages: LimitLine[] = [];
  daylimitMessages: LimitLine[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
      const success = params.get('successMessages');
      const limitErrors = params.get('limitMessages');
      const daylimitErrors = params.get('daylimitMessages');
      const errors = params.get('errorMessages');

      if (success) {
        try {
          this.successMessages = JSON.parse(success);
        } catch {
          this.successMessages = [success];
        }

        this.successMessages = this.successMessages.map((msg) => {
          const dateMatch = msg.match(/(\d{1,2})\s*\/\s*(\d{1,2})/);
          return dateMatch ? `${dateMatch[1]}/${dateMatch[2]}` : msg;
        });
      }

      if (errors) {
        try {
          this.errorMessages = JSON.parse(errors);
        } catch {
          this.errorMessages = [errors];
        }

        this.errorMessages = this.errorMessages.map((msg) => {
          const dateMatch = msg.match(/(\d{1,2})\s*\/\s*(\d{1,2})/);
          if (!dateMatch) return msg;

          const date = `${dateMatch[1]}/${dateMatch[2]}`;

          return `${date}`;
        });
      }

      if (limitErrors) {
        this.limitMessages = parseList(limitErrors).map((msg) => ({
          date: shortDate(msg),
          label: limitLabel(msg) ?? 'Weekly limit reached',
        }));
      }

      if (daylimitErrors) {
        this.daylimitMessages = parseList(daylimitErrors).map((msg) => ({
          date: shortDate(msg),
          label: limitLabel(msg),
        }));
      }
    });
  }

  onRedirect() {
    this.router.navigate(['my-reservations']);
  }
}

/** One skipped week on the limit lists: its date and, when known, why. */
interface LimitLine {
  date: string;
  /** Translation key; null keeps the legacy date only line. */
  label: string | null;
}

function parseList(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [raw];
  } catch {
    return [raw];
  }
}

/** Legacy messages carry "d / m"; subscription and facility caps an ISO date. */
function shortDate(msg: string): string {
  const legacy = msg.match(/(\d{1,2})\s*\/\s*(\d{1,2})/);
  if (legacy) return `${legacy[1]}/${legacy[2]}`;
  const iso = msg.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${Number(iso[3])}/${Number(iso[2])}`;
  return msg;
}

/** Key of the cap that skipped the week, for the RPCs' newer messages. */
function limitLabel(msg: string): string | null {
  if (msg.startsWith('Facility limit reached')) return 'Facility limit reached.';
  const sub = msg.match(/^(Daily|Weekly|Monthly|Total) subscription limit reached/);
  return sub ? `${sub[1]} subscription limit reached.` : null;
}
